import { useEffect, useState } from 'react';
import { householdService } from '@/services';

/**
 * Returns the count of emails the household has APPROVED this month
 * (status = PROCESSED and processed_disposition = APPROVED, within
 * the current tier window). Backed by `v_email_approved_monthly`.
 * Dismissed emails do NOT count — only approves consume quota. Used
 * by the Plan usage meter and the sidebar household section.
 *
 * Function name preserved for caller compatibility despite the
 * semantic shift; the source-of-truth swap happened in migration
 * 20260515000001.
 *
 * Returns null while loading; consumers should render a placeholder.
 *
 * Pass `refreshKey` to force a re-fetch after a side-effect that the
 * hook can't observe — e.g. an email-approval succeeded and the
 * sidebar should reflect the new count without a full reload. Bump the
 * key (any monotonically changing value works) and the effect re-runs.
 */
export function useMonthlyEmailsReviewed(
  householdId: string | null | undefined,
  refreshKey?: number,
): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!householdId) {
      setCount(null);
      return;
    }
    let cancelled = false;
    householdService
      .getMonthlyEmailsReviewed(householdId)
      .then((value) => {
        if (!cancelled) setCount(value);
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, refreshKey]);

  return count;
}
